// Card number, expiry and CVV formatting shared by the card entry screens.

export const digitsOnly = value => String(value || '').replace(/\D/g, '');

// Amex is the only network here with a 15-digit number and a 4-digit code; everything else is
// 13-19 digits with a 3-digit CVV. Driving both from the entered number means a Visa or
// Mastercard cardholder cannot type a 4th CVV digit, and an Amex cardholder still can.
export const isAmex = digits => /^3[47]/.test(digits);
export const maxCardDigits = digits => (isAmex(digits) ? 15 : 19);
export const expectedCvvLength = digits => (isAmex(digits) ? 4 : 3);

// Group the number as it is written on the card so a 16-digit string stays readable.
export const formatCardNumber = value => {
  const digits = digitsOnly(value);

  if (isAmex(digits)) {
    const capped = digits.slice(0, 15);
    return [capped.slice(0, 4), capped.slice(4, 10), capped.slice(10, 15)]
      .filter(Boolean)
      .join(' ');
  }

  return digits.slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
};

// MM/YY, as the expiry is printed on the card. Rebuilt from the digits rather than patching
// separators in place, so a stray second slash cannot swallow the year. A pasted or autofilled
// MM/YYYY is shortened to its last two year digits instead of being cut off after "MM/20".
export const formatShortExpiry = value => {
  let digits = digitsOnly(value);
  if (digits.length === 6) digits = `${digits.slice(0, 2)}${digits.slice(4)}`;
  digits = digits.slice(0, 4);

  if (digits.length <= 2) {
    // Keep a separator the cardholder just typed so they can carry on into the year, but let
    // them delete back past it.
    return digits.length === 2 && String(value).endsWith('/') ? `${digits}/` : digits;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// The card APIs take a four-digit year, so MM/YY becomes MMYYYY (e.g. "12/28" -> "122028").
// Anything other than a complete MM/YY comes back as its digits and fails validation.
export const expandExpiry = value => {
  const digits = digitsOnly(value);
  return digits.length === 4 ? `${digits.slice(0, 2)}20${digits.slice(2)}` : digits;
};
